import * as React from 'react';
import { useEffect } from 'react';

import { useIntl } from 'react-intl';

import { LottieAnimation } from 'components/animation';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import {
  PULLING_ANIMATION_HEIGHT,
  PULLING_ANIMATION_WIDTH,
  REFRESHING_ANIMATION_HEIGHT,
  REFRESHING_ANIMATION_WIDTH,
  RELEASING_ANIMATION_HEIGHT,
  RELEASING_ANIMATION_WIDTH,
} from './constants';
import {
  PullDownMessage,
  PullToRefreshContainer,
  ReleaseMessage,
} from './pull-to-reload-content.styled';

const Action = {
  PullingDown: 'pullingDown',
  Refreshing: 'refreshing',
  Releasing: 'releasing',
} as const;

type Values<T> = T[keyof T];

type Actions = Values<typeof Action>;

const getAnimationHeight = (action: Actions): string => {
  const animationHeight: Record<Actions, string> = {
    releasing: RELEASING_ANIMATION_HEIGHT,
    pullingDown: PULLING_ANIMATION_HEIGHT,
    refreshing: REFRESHING_ANIMATION_HEIGHT,
  };
  return animationHeight[action];
};

const getAnimationWidth = (action: Actions): string => {
  const animationHeight: Record<Actions, string> = {
    releasing: RELEASING_ANIMATION_WIDTH,
    pullingDown: PULLING_ANIMATION_WIDTH,
    refreshing: REFRESHING_ANIMATION_WIDTH,
  };
  return animationHeight[action];
};

export interface IPullToRefreshContent {
  animationData: { [key: string]: any };
  action: Actions;
  className?: string;
}

export const PullToReloadContent = ({
  animationData,
  action,
  className,
}: IPullToRefreshContent) => {
  const isPullingDown = action === Action.PullingDown;
  const isRefreshing = action === Action.Refreshing;
  const isReleasing = action === Action.Releasing;
  const { formatMessage } = useIntl();

  const defaultOptions = {
    animationData,
    autoplay: true,
    loop: true,
  };

  useEffect(() => {
    if (isReleasing) {
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    }
  }, [isReleasing]);

  return (
    <PullToRefreshContainer isRefreshing={isRefreshing} className={className}>
      {isPullingDown && <PullDownMessage>{formatMessage({ id: 'pullToRefresh' })}</PullDownMessage>}
      {isReleasing && <ReleaseMessage>{formatMessage({ id: 'releaseToRefresh' })}</ReleaseMessage>}
      <LottieAnimation
        ariaRole="presentation"
        options={defaultOptions}
        height={getAnimationHeight(action)}
        width={getAnimationWidth(action)}
      />
    </PullToRefreshContainer>
  );
};
